import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import { formatDate } from 'commons/utils/date'
import {
  type HeadItemType,
  INITIAL_PAGE,
  ITEMS_PER_PAGE,
  LoadingHavaianas,
  type PaginationParams,
  Table,
  Pagination as TablePagination,
} from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { toast } from 'react-toastify'
import { getClusterTracking, type TrackingData } from 'services/leadTimes'

import * as S from './styles'

interface TrackingModalProps {
  setOpen: Dispatch<SetStateAction<boolean>>
}

export const TrackingModal = ({ setOpen }: TrackingModalProps) => {
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationParams>({
    currentPage: INITIAL_PAGE,
    totalItems: 0,
  })
  const [trackingData, setTrackingData] = useState<TrackingData[]>([])

  const tableLabels: HeadItemType[] = [
    { columnName: 'Data', label: 'Data' },
    { columnName: 'Hora', label: 'Hora' },
    { columnName: 'Usuario', label: 'Usuário' },
    { columnName: 'Tecnologia', label: 'Tecnologia' },
    { columnName: 'Operacao', label: 'Operação' },
    { columnName: 'Descricao', label: 'Descrição' },
  ]

  const fetchTrackingData = async (page: number) => {
    setLoading(true)
    try {
      const { pagination, data } = await getClusterTracking({
        page,
        perPage: ITEMS_PER_PAGE,
      })
      setPagination(pagination)
      setTrackingData(data)
    } catch (error) {
      toast.error('Erro ao carregar tracking. Tente novamente mais tarde.')
      setOpen(false)
      setPagination({ currentPage: INITIAL_PAGE, totalItems: 0 })
      setTrackingData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTrackingData(pagination.currentPage)
  }, [pagination.currentPage])

  const tableData = () => {
    if (!trackingData.length) return []

    return trackingData.map((item) => {
      return {
        content: [
          formatDate(item.date, 'DD/MM/YYYY'),
          formatDate(item.date, 'HH:mm'),
          item.user,
          item.technology,
          item.type,
          item.description,
        ],
        id: item.date,
      }
    })
  }

  const handleClose = () => {
    setPagination({ ...pagination, currentPage: INITIAL_PAGE })
    setOpen(false)
  }

  const handlePageChange = (page: number) => {
    setPagination({
      ...pagination,
      currentPage: page,
    })
  }

  return (
    <ModalContentLayout
      closeAction={handleClose}
      closeButton
      description="Acompanhe todas as atualizações realizadas."
      title="Tracking"
      titleSize={22}
      width={'95vw'}
    >
      {loading ? (
        <S.LoadingWrapper>
          <LoadingHavaianas />
        </S.LoadingWrapper>
      ) : (
        <>
          <Table bodyData={tableData()} fixedHead headData={tableLabels} rowHeight={48} />
          <TablePagination
            currentPage={pagination?.currentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            numberOfItems={pagination?.totalItems}
            setCurrentPage={handlePageChange}
          />
        </>
      )}
    </ModalContentLayout>
  )
}
